import * as React from 'react'

import tinycolor from 'tinycolor2'

import { PRIMARY_PURPLE } from '../../colors'
import durationToString from '../../helpers/audio/durationToString'
import Slider from '../../ui/Slider'
import { primaryColorStyle } from '../../ui/theme'

interface ProgressBarProps {
  darkModeEnabled: boolean
  color: string
  duration: number | null
  progressDecimal: number
  hasTrack?: boolean
  fileNotFound?: boolean
  onSeek: (time: number) => void
}

const ProgressBar: React.FC<ProgressBarProps> = ({
  darkModeEnabled,
  color,
  duration,
  progressDecimal,
  hasTrack,
  fileNotFound,
  onSeek,
}) => {
  // While scrubbing, show the drag position instead of the polled progress
  // so the 50ms progress updates don't fight the thumb.
  const [scrubTime, setScrubTime] = React.useState<number | null>(null)

  const currentTime = scrubTime ?? progressDecimal * (duration ?? 0)

  const durationValid = Boolean(
    !Number.isNaN(Number(duration)) &&
      duration &&
      Number.isFinite(Number(duration)),
  )

  const handleChange = (_event: unknown, newValue: number | number[]) => {
    setScrubTime(newValue as number)
    onSeek(newValue as number)
  }

  const handleChangeCommitted = (
    _event: unknown,
    newValue: number | number[],
  ) => {
    onSeek(newValue as number)
    setScrubTime(null)
  }

  if (!durationValid || duration === null) {
    return null
  }

  const backgroundColor = tinycolor(color || PRIMARY_PURPLE)
    .setAlpha(0.6)
    .toRgbString()

  const textColor = tinycolor
    .mostReadable(
      darkModeEnabled
        ? tinycolor(color).darken(30)
        : tinycolor(color).lighten(30),
      ['#333', '#fff'],
    )
    .toHexString()

  return (
    <div
      className="z-[1] mx-auto flex w-full flex-row items-center justify-between"
      style={{ backgroundColor }}
    >
      <span
        className="shrink-0 whitespace-nowrap text-[0.85rem] tabular-nums"
        style={{ color: textColor, letterSpacing: '0.0333rem' }}
      >
        {durationToString(currentTime)}
      </span>

      <Slider
        size="small"
        value={Math.min(currentTime, duration)}
        onChange={handleChange}
        onChangeCommitted={handleChangeCommitted}
        className="[&_*]:!transition-none"
        style={primaryColorStyle(color)}
        min={0}
        max={duration}
        step={0.1}
        disabled={!hasTrack || fileNotFound}
      />

      <span
        className="shrink-0 whitespace-nowrap text-[0.85rem] tabular-nums"
        style={{ color: textColor, letterSpacing: '0.0333rem' }}
      >
        -{durationToString(duration - currentTime)}
      </span>
    </div>
  )
}

export default ProgressBar
